import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import CancelWarningModal from 'src/dispensary-admin/menu/modals/cancel-warning-modal';

/**
 * @param {object} props
 * @param {boolean} props.when
 * @param {(props: { isOpen: boolean, onClickYes: () => void, onClickNo: () => void }) => JSX.Element} [props.modal]
 * @param {(...args: any[]) => void} [props.handleConfirm]
 * @param {(...args: any[]) => void} [props.handleDeny]
 * @param {string[]} [props.blockedQueryParams] Query params that, when changed, should still
 *   trigger the unsaved changes prompt even though the pathname stays the same (e.g. switching
 *   the `category` being edited discards its in-progress changes just like navigating away).
 */
function RouteLeavingGuard({
  when,
  modal: Modal = CancelWarningModal,
  handleConfirm = _.noop,
  handleDeny = _.noop,
  blockedQueryParams = [],
}) {
  const [modalVisible, setModalVisible] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const history = useHistory();

  // when no is clicked, call the handleDeny func
  // then navigate to next location
  const handleDenyClick = async () => {
    await handleDeny();
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  // when yes is clicked, call the handleConfirm func
  // then navigate to the next location
  const handleConfirmClick = async () => {
    await handleConfirm();
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  // when close is clicked, just close the modal
  // and don't navigate anywhere
  const handleClose = () => {
    setModalVisible(false);
  };

  // when this function returns false, navigation is blocked completely
  const handleBlockedNavigation = (nextLocationFromPrompt) => {
    if (confirmedNavigation) {
      return true;
    }

    const isSamePathname = nextLocationFromPrompt.pathname === history.location.pathname;

    // A change to a caller-designated "blocked" query param (e.g. `category`)
    // switches which record is being edited, discarding the in-progress
    // changes the same way a real navigation would, so it must still prompt.
    const currentSearchParams = new URLSearchParams(history.location.search);
    const nextSearchParams = new URLSearchParams(nextLocationFromPrompt.search);
    const changesBlockedParam = blockedQueryParams.some(
      (param) => currentSearchParams.get(param) !== nextSearchParams.get(param)
    );

    // Query param only updates (e.g. filter/search/sort changes) on the same
    // page replace the URL via history but aren't actually navigation away
    // from the page, so they shouldn't trigger the unsaved changes prompt.
    if (isSamePathname && !changesBlockedParam) {
      return true;
    }

    setModalVisible(true);
    setNextLocation(nextLocationFromPrompt);
    return false;
  };

  useEffect(() => {
    if (confirmedNavigation && nextLocation) {
      // Navigate to the previous blocked location
      history.push(nextLocation.pathname + nextLocation.search);
      setConfirmedNavigation(false);
    }
  }, [confirmedNavigation, nextLocation, history]);

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />
      {modalVisible && (
        <Modal isOpen onClickYes={handleConfirmClick} onClickNo={handleDenyClick} onClose={handleClose} />
      )}
    </>
  );
}

export default RouteLeavingGuard;

// import RouteLeavingGuard from 'src/routes/route-leaving-guard';
// <RouteLeavingGuard
//   when={hasChanged}
//   modal={CancelWarningModal}
//   handleConfirm={this.handleModalClickYes}
//   handleDeny={this.handleModalClickNo}
// />
